import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Search, ChevronDown, ChevronUp, HelpCircle, DollarSign, FileText, Shield, Camera } from 'lucide-react';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const faqCategories = [
    {
      id: 'getting-started',
      icon: HelpCircle,
      title: 'Getting Started',
      faqs: [
        {
          question: 'How do I create an account?',
          answer: 'Click "Get Started" on the homepage and select whether you\'re a creative or a client. Fill in your details, accept the terms and conditions, and you\'re ready to go. Creatives should complete their profile with portfolio items to start receiving booking requests.'
        },
        {
          question: 'What\'s the difference between a creative and a client account?',
          answer: 'Creative accounts are for photographers and videographers who want to offer their services. Client accounts are for businesses and organizations looking to hire creatives for projects. Each account type has different features tailored to their needs.'
        },
        {
          question: 'How do I set up my creative profile?',
          answer: 'After signing up, go to "Edit Profile" to add your professional title, bio, specialties, hourly rate, and portfolio items. The more complete your profile, the more likely clients are to book you.'
        }
      ]
    },
    {
      id: 'bookings',
      icon: Camera,
      title: 'Bookings & Projects',
      faqs: [
        {
          question: 'How do bookings work?',
          answer: 'Clients can either post a project for creatives to apply to, or directly request a booking from a creative\'s profile. Once both parties agree, they create a project agreement that outlines deliverables, timeline, payment, and revision limits.'
        },
        {
          question: 'Can I withdraw a booking request?',
          answer: 'Yes, clients can withdraw booking requests before the creative accepts. Creatives can also withdraw from projects before an agreement is signed. Once an agreement is signed and payment is made, withdrawals follow the agreement terms.'
        },
        {
          question: 'How do I set my availability?',
          answer: 'Go to the "Availability" page to set your schedule. You can mark recurring days off (like weekends), add specific unavailable dates, and set travel locations with date ranges so clients in those cities can find you.'
        }
      ]
    },
    {
      id: 'payments',
      icon: DollarSign,
      title: 'Payments & Pricing',
      faqs: [
        {
          question: 'How does payment work?',
          answer: 'We use a secure escrow system. When a client approves an agreement, they pay the full amount which is held securely. Once the creative delivers the work and the client approves it, the payment is released to the creative (minus the 5% platform fee).'
        },
        {
          question: 'When do I get paid as a creative?',
          answer: 'Payment is released when the client approves your deliverables. Make sure your Stripe account is connected in your profile settings to receive payouts. Funds typically arrive within 2-3 business days after release.'
        },
        {
          question: 'What if I\'m not satisfied with the work?',
          answer: 'Each project includes a set number of revisions agreed upon in the project agreement. If you\'ve used all revisions and still need changes, you can request additional revisions from the creative at an additional cost.'
        },
        {
          question: 'Is there a platform fee?',
          answer: 'Yes, we charge a 5% platform fee on completed projects. This fee is deducted from the payment to the creative when deliverables are approved. Clients pay the agreed project amount with no additional fees.'
        }
      ]
    },
    {
      id: 'agreements',
      icon: FileText,
      title: 'Agreements & Deliverables',
      faqs: [
        {
          question: 'What is a project agreement?',
          answer: 'A project agreement is a mutual confirmation between you and the other party about project scope, deliverables, timeline, payment amount, and number of revisions included. Both parties must sign before work begins.'
        },
        {
          question: 'How do I deliver my work?',
          answer: 'As a creative, you\'ll share your deliverables via external links (Google Drive, Dropbox, etc.). Share the files as view-only initially, and enable downloads after the client approves the work.'
        },
        {
          question: 'What happens after deliverables are submitted?',
          answer: 'The client reviews your work and can either approve it (releasing payment) or request revisions (if revisions remain). If all revisions are used, additional revisions can be purchased.'
        }
      ]
    },
    {
      id: 'safety',
      icon: Shield,
      title: 'Trust & Safety',
      faqs: [
        {
          question: 'Is my payment secure?',
          answer: 'Yes, all payments are processed through Stripe\'s secure payment system and held in escrow until work is delivered and approved. Your financial information is never stored on our servers.'
        },
        {
          question: 'What if there\'s a dispute?',
          answer: 'If you have a dispute with the other party, first try to resolve it through direct communication. If needed, contact us and we\'ll review the situation based on the project agreement and help facilitate a resolution.'
        },
        {
          question: 'How are creatives verified?',
          answer: 'Creatives build their reputation through verified reviews from completed projects. We also offer badges for top-rated creatives and trending professionals based on platform activity.'
        }
      ]
    }
  ];

  const filteredCategories = searchQuery
    ? faqCategories.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.faqs.length > 0)
    : faqCategories;

  return (
    <div className="min-h-screen bg-[#f7f5f2]">

      {/* Page Header */}
      <div className="bg-white border-b border-[#e4e1db] px-5 md:px-10 pt-7 pb-5">
        <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-1.5">Support</p>
        <h1 className="font-display font-normal text-[28px] md:text-[32px] text-[#0e0e0e] tracking-[-0.5px] leading-[1.1]">
          Help Center
        </h1>
      </div>

      <div className="px-5 md:px-10 py-6 max-w-full space-y-5 pb-20">

        {/* Search */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-6">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">How can we help?</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-4">Search our FAQ or browse by category below.</p>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c0bdb8]" />
            <Input
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 text-[14px] text-[#0e0e0e] border-[#e4e1db] placeholder:text-[#c0bdb8] rounded-full focus-visible:ring-0 focus-visible:border-[#0e0e0e]"
              data-testid="help-search-input"
            />
          </div>
        </div>

        {/* No results */}
        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full bg-white border border-[#e4e1db] flex items-center justify-center mx-auto mb-3">
              <HelpCircle className="w-5 h-5 text-[#c0bdb8]" />
            </div>
            <p className="text-[14px] font-medium text-[#0e0e0e]">No results for "{searchQuery}"</p>
            <p className="text-[12px] text-[#a0a0a0] mt-1">Try a different search term or browse the categories below</p>
          </div>
        )}

        {/* FAQ Categories */}
        {filteredCategories.map((category) => (
          <div key={category.id} className="bg-white border border-[#e4e1db] rounded-[20px] overflow-hidden">
            <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-[#e4e1db]">
              <div className="w-8 h-8 rounded-[8px] bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center flex-shrink-0">
                <category.icon className="w-4 h-4 text-[#5a5a5a]" />
              </div>
              <h2 className="font-display font-normal text-[18px] text-[#0e0e0e] tracking-[-0.3px]">{category.title}</h2>
            </div>
            <div>
              {category.faqs.map((faq, idx) => {
                const faqKey = `${category.id}-${idx}`;
                const isOpen = openFaq === faqKey;
                return (
                  <div key={idx} className="border-b border-[#e4e1db] last:border-0">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : faqKey)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-[#f7f5f2] transition-colors"
                    >
                      <span className="text-[14px] font-medium text-[#0e0e0e] pr-4">{faq.question}</span>
                      {isOpen
                        ? <ChevronUp className="w-4 h-4 text-[#a0a0a0] flex-shrink-0" />
                        : <ChevronDown className="w-4 h-4 text-[#a0a0a0] flex-shrink-0" />
                      }
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <p className="text-[13px] text-[#5a5a5a] leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7 text-center">
          <div className="w-10 h-10 rounded-[10px] bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-[#5a5a5a]">
              <rect x="2" y="4" width="16" height="13" rx="2"/><path d="M2 7l8 5 8-5"/>
            </svg>
          </div>
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Still need help?</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-5 max-w-sm mx-auto leading-relaxed">
            Can't find what you're looking for? Reach out and we'll look into it for you.
          </p>
          <a
            href="mailto:support@framefinder.io"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#0e0e0e] text-white text-[13px] font-medium hover:bg-[#222] transition-colors"
          >
            Email Support
          </a>
          <p className="text-[11px] text-[#c0bdb8] mt-3">support@framefinder.io</p>
        </div>

      </div>
    </div>
  );
};

export default HelpCenter;
